import debounce from "lodash/debounce";

type VisibilityKey = string;

type VisibilityArgs = {
    itemID: string | null;
    layerID: string | null;
    visible: boolean;
    // retry
    attemptsLeft: number;
    retryDelayMs: number;
};

const debouncers = new Map<VisibilityKey, ReturnType<typeof debounce>>();
const retryTimers = new Map<VisibilityKey, number>();

function getKey(itemID: string | null, layerID: string | null): VisibilityKey {
    return `${layerID ?? ""}::${itemID ?? ""}` || "__global__";
}

/** 
This function controls the display of icons on the map, so that changes do not overlap, since the DOM always lags behind the code.
   * @param {string | null} itemID The ID of the icon to be shown or hidden. If null, all icons associated with the layerID will be processed.
   * @param {string | null} layerID The ID of the layer. If null, the itemID will be used to find the icon.
   * @param {boolean} visible Whether the icon should be visible or not. If true, the icon will be shown. If false, it will be hidden.
   */
function applyVisibilityOnce(itemID: string | null, layerID: string | null, visible: boolean): boolean {
    const displayValue = visible ? "" : "none";
    let foundSomething = false;

    if (layerID) {
        const icons = document.getElementsByClassName(layerID);
        if (icons.length > 0) foundSomething = true;
        for (let i = 0; i < icons.length; i++) {
            (icons[i] as HTMLElement).style.display = displayValue;
        }
    }

    if (itemID) {
        const icon = document.getElementById(itemID) as HTMLElement | null;
        if (icon) {
            foundSomething = true;
            icon.style.display = displayValue;
        }
    }

    return foundSomething;
}

function clearRetry(key: VisibilityKey) {
    const t = retryTimers.get(key);
    if (t != null) window.clearTimeout(t);
    retryTimers.delete(key);
}

function scheduleApply(args: VisibilityArgs) {
    const key = getKey(args.itemID, args.layerID);

    clearRetry(key);

    const found = applyVisibilityOnce(args.itemID, args.layerID, args.visible);
    if (found) return;

    if (args.attemptsLeft <= 0) return;

    const timer = window.setTimeout(() => {
        scheduleApply({ ...args, attemptsLeft: args.attemptsLeft - 1 });
    }, args.retryDelayMs);

    retryTimers.set(key, timer);
}

/**
 * Debounced per key + short retry window to wait for DOM nodes to exist.
 */
export function setVisibilityIconDebounced(
    itemID: string | null,
    layerID: string | null,
    visible: boolean,
    opts?: {
        debounceMs?: number;
        retryDelayMs?: number;
        maxAttempts?: number;
    }
) {
    const debounceMs = opts?.debounceMs ?? 30;
    const retryDelayMs = opts?.retryDelayMs ?? 50;
    const maxAttempts = opts?.maxAttempts ?? 10; // 10 * 50ms = 500ms total retry window

    const key = getKey(itemID, layerID);

    let fn = debouncers.get(key);
    if (!fn) {
        fn = debounce((payload: Omit<VisibilityArgs, "attemptsLeft" | "retryDelayMs"> & { retryDelayMs: number; maxAttempts: number }) => {
            scheduleApply({
                itemID: payload.itemID,
                layerID: payload.layerID,
                visible: payload.visible,
                attemptsLeft: payload.maxAttempts,
                retryDelayMs: payload.retryDelayMs,
            });
        }, debounceMs);
        debouncers.set(key, fn);
    }

    fn({ itemID, layerID, visible, retryDelayMs, maxAttempts });
}

export function cancelVisibility(keyItemID: string | null, keyLayerID: string | null) {
    const key = getKey(keyItemID, keyLayerID);
    debouncers.get(key)?.cancel();
    debouncers.delete(key);
    clearRetry(key);
}
